import axios from 'axios';
import { useCallback, useState } from 'react';

type SecureDetailsTokenResponse = {
  token: string;
  encryptedSymmetricKey: string;
  initialisationVector: string;
  secureDetailsUrl: string;
};

type SecureCardDetails = {
  pan: string;
  cvv2: string;
};

type UseSecureCardDetailsReturn = {
  details: SecureCardDetails | null;
  isLoading: boolean;
  error: string | null;
  revealDetails: (cardId: string) => Promise<void>;
  copyToClipboard: (value: string) => Promise<void>;
  reset: () => void;
};

/**
 * Generates an RSA key pair suitable for the Modulr secure card details API.
 * Uses WebCrypto API to generate a 4096-bit RSA-OAEP key pair.
 */
const generateKeyPair = async (): Promise<CryptoKeyPair> => {
  return crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt'],
  );
};

/**
 * Encodes the public key as a Base64 string for the Modulr API.
 */
const encodePublicKey = async (keyPair: CryptoKeyPair): Promise<string> => {
  const exported = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

/**
 * Decrypts the access token returned by the Modulr API.
 * Uses the private key to decrypt the symmetric key, then uses the symmetric key
 * to decrypt the token using AES-GCM.
 */
const decryptAccessToken = async (
  encryptedToken: string,
  encryptedSymmetricKey: string,
  encodedIv: string,
  privateKey: CryptoKey,
): Promise<string> => {
  // Decode and decrypt the symmetric key using RSA-OAEP
  const decodedEncryptedSymmetricKey = Uint8Array.from(atob(encryptedSymmetricKey), (c) => c.charCodeAt(0));
  const decryptedSymmetricKey = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    decodedEncryptedSymmetricKey,
  );

  // Import the symmetric key as an AES-GCM key
  const aesKey = await crypto.subtle.importKey('raw', decryptedSymmetricKey, 'AES-GCM', true, ['encrypt', 'decrypt']);

  // Decode the IV and encrypted token
  const decodedIv = Uint8Array.from(atob(encodedIv), (c) => c.charCodeAt(0));
  const decodedEncryptedToken = Uint8Array.from(atob(encryptedToken), (c) => c.charCodeAt(0));

  // Decrypt the token using AES-GCM
  const rawDecryptedToken = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: decodedIv,
    },
    aesKey,
    decodedEncryptedToken,
  );

  return new TextDecoder('utf-8').decode(new DataView(rawDecryptedToken));
};

export const useSecureCardDetails = (): UseSecureCardDetailsReturn => {
  const [details, setDetails] = useState<SecureCardDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revealDetails = useCallback(async (cardId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Generate RSA key pair
      const keyPair = await generateKeyPair();

      // Step 2: Encode the public key
      const publicKey = await encodePublicKey(keyPair);

      // Step 3: Request secure details token from our server
      const tokenResponse = await axios.post<SecureDetailsTokenResponse>('/api/cards/secure-details-token', {
        cardId,
        publicKey,
      });

      const { token, encryptedSymmetricKey, initialisationVector, secureDetailsUrl } = tokenResponse.data;

      // Step 4: Decrypt the token
      const decryptedToken = await decryptAccessToken(
        token,
        encryptedSymmetricKey,
        initialisationVector,
        keyPair.privateKey,
      );

      // Step 5: Call Modulr's secure-detail endpoint directly
      const detailsResponse = await fetch(secureDetailsUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          Accept: 'application/json',
        },
      });

      if (!detailsResponse.ok) {
        const errorText = await detailsResponse.text();
        throw new Error(`Failed to fetch card details: ${detailsResponse.status} - ${errorText}`);
      }

      const modulrDetails = (await detailsResponse.json()) as { pan: string; cvv2: string };

      setDetails({
        pan: modulrDetails.pan,
        cvv2: modulrDetails.cvv2,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reveal card details';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const copyToClipboard = useCallback(async (value: string) => {
    await navigator.clipboard.writeText(value);
  }, []);

  const reset = useCallback(() => {
    setDetails(null);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    details,
    isLoading,
    error,
    revealDetails,
    copyToClipboard,
    reset,
  };
};
